import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

// Consultant invoices and KRA eTIMS receipts (user_mngt finance module;
// lms_backend docs/api/finance-invoices.md). Sensitive:
// - every query is keyed by the signed-in administrator and dropped as soon
//   as nothing shows it (cacheTime 0), and all of them are removed when the
//   administrator changes or signs out (useFinanceCacheGuard);
// - download links are fetched on click and never cached or stored;
// - nothing here is logged or written to browser storage.
const BASE = '/finance';
export const PAGE_SIZE = 50;

const options = { refetchOnWindowFocus: false, retry: false, cacheTime: 0 };

const clean = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

const useWho = () =>
  useSelector(
    (state) => state.auth.userDetails?.id || state.auth.userDetails?.email || ''
  );

const useKey = (...parts) => ['finance', useWho(), ...parts];

// Drops every finance query when the administrator changes or signs out
// (the sidebar unmounts then), so nobody sees someone else's data.
export const useFinanceCacheGuard = () => {
  const queryClient = useQueryClient();
  const who = useWho();
  useEffect(
    () => () => queryClient.removeQueries(['finance']),
    [who, queryClient]
  );
};

// Whether the signed-in administrator holds finance access. Not audited.
// Any error (including a missing route) means "no".
export const useFinanceAccess = () =>
  useQuery(
    useKey('access'),
    async () =>
      Boolean((await axiosInstancev2.get(`${BASE}/access`)).data?.holder),
    { refetchOnWindowFocus: false, retry: false, staleTime: 5 * 60 * 1000 }
  );

// ---- self ----

export const useMyInvoices = (offset = 0) => {
  const params = { limit: PAGE_SIZE, offset };
  return useQuery(
    useKey('my', 'list', params),
    async () =>
      (await axiosInstancev2.get(`${BASE}/my-invoices`, { params })).data,
    { ...options, keepPreviousData: true }
  );
};

// ---- holders ----

export const useInvoices = (filters, offset = 0) => {
  const params = clean({
    period: filters.period,
    administrator_id: filters.administratorId,
    status: filters.status,
    q: filters.q,
    limit: PAGE_SIZE,
    offset,
  });
  return useQuery(
    useKey('all', 'list', params),
    async () =>
      (await axiosInstancev2.get(`${BASE}/invoices`, { params })).data,
    { ...options, keepPreviousData: true }
  );
};

export const useInvoice = (id, scope) =>
  useQuery(
    useKey(scope, 'one', id),
    async () =>
      (
        await axiosInstancev2.get(
          scope === 'my'
            ? `${BASE}/my-invoices/${id}`
            : `${BASE}/invoices/${id}`
        )
      ).data,
    { ...options, enabled: Boolean(id) }
  );

export const useMissing = (period) =>
  useQuery(
    useKey('all', 'missing', period),
    async () =>
      (
        await axiosInstancev2.get(`${BASE}/invoices/missing`, {
          params: { period },
        })
      ).data,
    { ...options, enabled: Boolean(period) }
  );

export const useAudit = (filters, offset = 0) => {
  const params = clean({
    submission_id: filters.submissionId,
    actor_id: filters.actorId,
    limit: PAGE_SIZE,
    offset,
  });
  return useQuery(
    useKey('audit', params),
    async () => (await axiosInstancev2.get(`${BASE}/audit`, { params })).data,
    { ...options, keepPreviousData: true }
  );
};

export const useGrants = () =>
  useQuery(
    useKey('grants'),
    async () => (await axiosInstancev2.get(`${BASE}/grants`)).data,
    options
  );

export const useConsultants = (q, enabled = true) =>
  useQuery(
    useKey('consultants', q),
    async () =>
      (
        await axiosInstancev2.get(`${BASE}/consultants`, {
          params: clean({ q, limit: 20 }),
        })
      ).data?.consultants ?? [],
    { ...options, enabled }
  );

// ---- changes ----

const useFinanceMutation = (fn) => {
  const queryClient = useQueryClient();
  const who = useWho();
  return useMutation(fn, {
    onSuccess: () => queryClient.invalidateQueries(['finance', who]),
  });
};

// Create (no id) or update an invoice; `scope` is 'my' or 'all'.
export const useSaveInvoice = (scope) =>
  useFinanceMutation(async ({ id, form }) => {
    const path = scope === 'my' ? `${BASE}/my-invoices` : `${BASE}/invoices`;
    return (
      id
        ? await axiosInstancev2.put(`${path}/${id}`, form)
        : await axiosInstancev2.post(path, form)
    ).data;
  });

// Finance attaches a payslip, proof of payment or other document.
export const useAddDocument = () =>
  useFinanceMutation(
    async ({ id, form }) =>
      (await axiosInstancev2.post(`${BASE}/invoices/${id}/documents`, form))
        .data
  );

export const useSetStatus = () =>
  useFinanceMutation(
    async ({ id, status, note }) =>
      (
        await axiosInstancev2.put(
          `${BASE}/invoices/${id}/status`,
          clean({ status, note })
        )
      ).data
  );

export const useGrantAccess = () =>
  useFinanceMutation(
    async (body) =>
      (await axiosInstancev2.post(`${BASE}/grants`, clean(body))).data
  );

export const useRevokeAccess = () =>
  useFinanceMutation(
    async ({ administratorId, note }) =>
      (
        await axiosInstancev2.delete(`${BASE}/grants/${administratorId}`, {
          params: clean({ note }),
        })
      ).data
  );

// A short-lived (5 minute) link, fetched on click. Not cached or stored.
export const fetchDownloadUrl = async (scope, invoiceId, fileId) =>
  (
    await axiosInstancev2.get(
      `${BASE}/${
        scope === 'my' ? 'my-invoices' : 'invoices'
      }/${invoiceId}/files/${fileId}/download`
    )
  ).data?.url;

export const EXPORT_PATH = `${BASE}/invoices/export`;

export const financeError = (error, fallback) => {
  const data = error?.response?.data;
  return data?.message || data?.error || fallback;
};

// INVALID_FIELDS → { field: message }.
export const fieldProblems = (error) =>
  Object.fromEntries(
    (error?.response?.data?.details?.problems ?? []).map((p) => [
      p.field,
      p.message,
    ])
  );

// ---- monthly invoice reminders (holders) ----

export const useInvoiceReminderSettings = () =>
  useQuery(
    useKey('reminders', 'settings'),
    async () => (await axiosInstancev2.get(`${BASE}/reminders/settings`)).data,
    options
  );

export const useSaveInvoiceReminderSettings = () =>
  useFinanceMutation(
    async (changes) =>
      (await axiosInstancev2.put(`${BASE}/reminders/settings`, changes)).data
  );

// Without ids: everyone expected who's missing that month.
export const useSendInvoiceReminders = () =>
  useFinanceMutation(
    async ({ period, administratorIds }) =>
      (
        await axiosInstancev2.post(
          `${BASE}/reminders/send`,
          administratorIds?.length
            ? { period, administrator_ids: administratorIds }
            : { period }
        )
      ).data
  );

export const useExpected = () =>
  useQuery(
    useKey('expected'),
    async () =>
      (await axiosInstancev2.get(`${BASE}/expected`)).data?.administrators ??
      [],
    options
  );

export const useSetExpected = () =>
  useFinanceMutation(
    async ({ administratorId, mode }) =>
      (
        await axiosInstancev2.put(`${BASE}/expected/${administratorId}`, {
          mode,
        })
      ).data
  );
